import type { AiRessource, ButtonListAttachment, CarouselAttachment, FileDownloadAttachment, ImageAttachment, MediaDetails, WSBotMessage, WSButton } from "@/types/WSBotMessage"
import type { MessageContext } from "./parser"
import type { Intent, Operator } from "@/types"
import logger from "@/logger"
import { isString } from "@/common/utils"



export interface ConversationEntry {
  type: 'msg'
  messageType: 'TextMessage' | 'UserMessage' | 'ButtonList' | 'Carousel' | 'Media' | 'FileUpload' | 'FileDownload' | 'CoversationMessage'
  source: string
  operator: Operator
  uuid?: string
  key: string
  timestamp: number
  isGenerated: boolean
  resourcesUsed?: AiRessource[]
}

export type BotMessage = ConversationEntry & {
  messageType: 'TextMessage'
  data: {
    text: string
    html?: string
  }
}

export type UserMessage = ConversationEntry & {
  messageType: 'UserMessage'
  data: {
    text: string
  }
}

export type ButtonList = ConversationEntry & {
  messageType: 'ButtonList'
  data: {
    buttons: Array<PostbackButton | UrlPostbackButton | WebUrlButton>
  }
}

// WEB URL
/*       {
        "title": "moinAI Newsletter",
        "subtitle": "Alle 2 Wochen das wichtigste KI- und Chatbot-Wissen ins Postfach.",
        "buttons": [
          {
            "type": "web_url",
            "url": "https://www.moin.ai/resources/newsletter?utm_campaign=Moini&utm_source=moini&utm_medium=chatbot&utm_term=happy-path-slides",
            "title": "Jetzt abonnieren"
          }
        ],
        "image_url": "https://media.moin.ai/1668085396947_446625473-01_slide_newsletterpng",
        "media": {
          "url": "https://media.moin.ai/1668085396947_446625473-01_slide_newsletterpng",
          "type": "unknown"
        }
      }, */

// URL POSTBACK
/* {
    "type": "url_postback",
    "payload": {
        "name": "goal_faq_praxisbeispiele",
        "url": "https://www.moin.ai/case-studies/chatbot-faz-verlag?utm_campaign=Moini&utm_source=chatbot&utm_term=intent-praxisbeispiele",
        "parameters": {
            "confidence": 1
        }
    },
    "title": "📗FAZ: Chatbot im Marketing & Service"
}, */

// POSTBACK
/* {
  "title": "{title1588764134795}",
  "subtitle": "{subtitle1588764134795}",
  "image": "{image1588764134795}",
  "buttons": [
    {
      "type": "postback",
      "title": "{btntitle01588764134795}",
      "payload": {
        "name": "btn_1",
        "parameters": {
          "confidence": 1
        }
      }
    }
  ]
}, */

export type PostbackButton = {
  type: 'postback'
  text: string
  intent: Intent
}

export type UrlPostbackButton = {
  type: 'url_postback'
  url: string
  text: string
  intent: Intent
}
export type WebUrlButton = {
  type: 'web_url'
  url: string
  text: string
}

export type Button = PostbackButton | UrlPostbackButton | WebUrlButton

export type Card = {
  messageType: 'Card'
  headline: string
  text: string
  actions: Button[]
  media: MediaDetails
}

export type Carousel = ConversationEntry & {
  messageType: 'Carousel'
  data: {
    startIndex?: number
    items: Card[]
  }
}

export type Media = ConversationEntry & {
  messageType: 'Media'
  data: {
    mediaType: 'image' | 'video' | 'youtube'
    mediaUrl: string
    media?: MediaDetails
  }
}

export type FileDownload = ConversationEntry & {
  messageType: 'FileDownload'
  data: {
    fileinfo: {
      filename: string
      filesize: number
      filetype: string
    }
    fileUrl: string
  }
}

export type ConversationMessages = BotMessage | UserMessage | ButtonList | Carousel | Media | FileDownload | ConversationEntry

export function createMessageKey(timestamp: number, source: string, type: string) {
  return `${'msg'}-${timestamp}-${source}-${type}`
}

export function createBotMessage(rawmessage: WSBotMessage, context: MessageContext): BotMessage {
  if (!rawmessage?.message?.text) console.log('botmessage: empty message')
  return {
    messageType: 'TextMessage',
    ...context,
    timestamp: rawmessage.time,
    key: createMessageKey(rawmessage.time, context.source, 'TextMessage'),
    type: 'msg',
    isGenerated: !!rawmessage?.isGenerated,
    resourcesUsed: rawmessage?.resourcesUsed,
    data: {
      text: rawmessage?.message?.text || '',
      html: rawmessage?.message?.html
    }
  }
}

export function createUserMessage(rawmessage: WSBotMessage, context: MessageContext): UserMessage {
  if (!rawmessage?.message?.text) console.log('usermessage: empty message')
  return {
    messageType: 'UserMessage',
    ...context,
    timestamp: rawmessage.time,
    key: createMessageKey(rawmessage.time, context.source, 'UserMessage'),
    type: 'msg',
    isGenerated: !!rawmessage?.isGenerated,
    resourcesUsed: rawmessage?.resourcesUsed,
    data: { text: rawmessage?.message?.text || '' }
  }
}



function createPostbackButton(button: WSButton): PostbackButton | null {
  if (!button || button.type !== 'postback') {
    logger.error('createPostbackButton: unknown button type', button)
    return null
  }
  if (!button.payload || isString(button.payload)) {
    logger.error('createPostbackButton: unknown button payload', button)
    return null
  }
  return {
    type: 'postback',
    text: button.title,
    intent: {
      text: button.title,
      intent: {
        name: button.payload.name,
        parameters: button.payload.parameters
      }
    }
  }
}

function createUrlPostbackButton(button: WSButton): UrlPostbackButton | null {
  if (!button || button.type !== 'url_postback') {
    logger.error('createUrlPostbackButton: unknown button type', button)
    return null
  }
  if (!button.payload || isString(button.payload)) {
    logger.error('createUrlPostbackButton: unknown button payload', button)
    return null
  }
  const url = button.payload.url

  if (!url || !isString(url)) {
    logger.warn('createUrlPostbackButton: unknown button url', button)
    // we allow empty urls for url_postback buttons because
    // I saw lots of them in the wild
    // and we don't want to break the flow
  }
  return {
    type: 'url_postback',
    url: isString(url) ? url : '',
    text: button.title,
    intent: {
      text: button.title,
      intent: {
        name: button.payload.name,
        parameters: button.payload.parameters
      }
    }
  }
}

function createWebUrlButton(button: WSButton): WebUrlButton | null {
  if (!button || button.type !== 'web_url') {
    logger.error('createWebUrlButton: unknown button type', button)
    return null
  }

  if (!button.url || !isString(button.url)) {
    logger.warn('createWebUrlButton: unknown button url', button)
    // we allow empty urls for web_url buttons because
    // I saw lots of them in the wild
    // and we don't want to break the flow
  }
  return {
    type: 'web_url',
    url: isString(button.url) ? button.url : '',
    text: button.title
  }
}

function createButtons(buttons: WSButton[]) {
  if (!buttons) return []
  return buttons?.map((button) => {
    // https://grugbrain.dev/ mode enabled
    if (button.type === 'postback') {
      return createPostbackButton(button)
    } else if (button.type === 'url_postback') {
      return createUrlPostbackButton(button)
    } else if (button.type === 'web_url') {
      return createWebUrlButton(button)
    }
    logger.error('createButtonList: unknown button type', button)
    return null
  }).filter((button) => button !== null)
}


export function createButtonList(rawmessage: WSBotMessage, context: MessageContext): ButtonList {
  const message = rawmessage.message
  const attachment = message?.attachment as ButtonListAttachment

  const buttons = createButtons(attachment.payload?.buttons)

  return {
    messageType: 'ButtonList',
    ...context,
    timestamp: rawmessage.time,
    key: createMessageKey(rawmessage.time, context.source, 'ButtonList'),
    type: 'msg',
    isGenerated: !!rawmessage?.isGenerated,
    resourcesUsed: rawmessage?.resourcesUsed,
    data: { buttons }
  }
}

export function createImage(rawmessage: WSBotMessage, context: MessageContext): Media {
  const attachment = rawmessage?.message?.attachment as ImageAttachment
  const data = {
    mediaType: 'image' as const,
    mediaUrl: attachment.payload.url,
    media: attachment.payload.media
  }

  return {
    messageType: 'Media',
    ...context,
    timestamp: rawmessage.time,
    key: createMessageKey(rawmessage.time, context.source, 'Media-Image'),
    type: 'msg',
    isGenerated: !!rawmessage?.isGenerated,
    resourcesUsed: rawmessage?.resourcesUsed,
    data
  }
}

export function createVideo(rawmessage: WSBotMessage, context: MessageContext): Media {
  const attachment = rawmessage?.message?.attachment as ImageAttachment
  const data = {
    mediaType: attachment.payload.url.includes('youtube') ? 'youtube' as const : 'video' as const,
    mediaUrl: attachment.payload.url,
    media: attachment.payload.media
  }

  return {
    messageType: 'Media',
    ...context,
    timestamp: rawmessage.time,
    key: createMessageKey(rawmessage.time, context.source, 'Media-Video'),
    type: 'msg',
    isGenerated: !!rawmessage?.isGenerated,
    resourcesUsed: rawmessage?.resourcesUsed,
    data
  }
}

export function createFileDownload(rawmessage: WSBotMessage, context: MessageContext): FileDownload {
  const attachment = rawmessage?.message?.attachment as FileDownloadAttachment
  const filesize = Number(attachment.payload.filesize)
  if (isNaN(filesize)) console.error('filesize is NaN')
  const data = {
    fileinfo: {
      filename: attachment.payload.filename,
      filesize: filesize,
      filetype: attachment.payload.filetype
    },
    fileUrl: attachment.payload.url
  }

  return {
    messageType: 'FileDownload',
    ...context,
    timestamp: rawmessage.time,
    key: createMessageKey(rawmessage.time, context.source, 'FileDownload'),
    type: 'msg',
    isGenerated: !!rawmessage?.isGenerated,
    resourcesUsed: rawmessage?.resourcesUsed,
    data
  }
}

// this is to support legacy media attachment messages
const isYouTubeUrl = (url: string) => {
  return url.includes('youtube.com') || url.includes('youtu.be')
}

const isVideo = (url: string) => {
  return url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.webm')
}

const getMediaTypeFromUrl = (url: string) => {
  if (isYouTubeUrl(url)) return 'youtube'
  if (isVideo(url)) return 'video'
  return 'image'
}


export function createCarousel(rawmessage: WSBotMessage, context: MessageContext): Carousel {
  const message = rawmessage.message
  const attachment = message?.attachment as CarouselAttachment
  const items = attachment.payload.elements.map((item) => {

    const media: MediaDetails = item.media || {
      url: item.image_url,
      type: getMediaTypeFromUrl(item.image_url),
    }

    // @ts-expect-error type is not defined in the MediaDetails type but might appear
    if (media.type === 'unknown') {
      // this is a fallback for unknown media types
      // we assume it's an image if the type is unknown
      // this was the old behavior
      // and we don't want to break existing carousels
      logger.warn('createCarousel: unknown media type', media)
      media.type = 'image'
    }

    const card = {
      messageType: 'Card' as const,
      headline: item.title,
      text: item.subtitle,
      actions: createButtons(item?.buttons),
      media: media
    }
    return card
  })
  const data = {
    startIndex: 1,
    items
  }
  return {
    messageType: 'Carousel',
    ...context,
    timestamp: rawmessage.time,
    key: createMessageKey(rawmessage.time, context.source, 'Carousel'),
    type: 'msg',
    isGenerated: !!rawmessage?.isGenerated,
    resourcesUsed: rawmessage?.resourcesUsed,
    data
  }
}

export function createConversationInfo(text: string, context: MessageContext, timestamp?: number) {
  const data = { text }

  return {
    messageType: 'CoversationMessage',
    ...context,
    source: 'system',
    timestamp: timestamp || Date.now(),
    key: createMessageKey(timestamp || Date.now(), context.source, 'CoversationMessage'),
    type: 'msg',
    isGenerated: false,
    data
  }
}

export function createOptimisticUpdateUserMessage(text: string, tempId: string, timestamp?: number) {
  // build and extract message data
  const data = {
    messageType: 'UserMessage',
    text
  }

  return {
    messageType: 'UserMessage',
    timestamp: timestamp || Date.now(),
    key: createMessageKey(timestamp || Date.now(), 'user', 'CoversationMessage'),
    type: 'msg',
    isGenerated: false,
    uuid: tempId,
    source: 'user',
    data
  }
}
